/**
 * @fileoverview
 * Contains an action creator that sets up the application.
 */

import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { Store } from "Store/index";
import { MediaQueryTracker } from "Lib/ts/media_query_tracker";
import {
  UpdateStatus,
  SetPreviewFlag,
  SetRoutes,
  MapRoutes
} from "Store/application";
import { navigate } from "Action_creators/navigate";
import { setActiveRoute } from "Action_creators/set_active_route";
import { setRoutes } from "./set_routes";
import { SetPages } from "Store/pages";
import { UpdateMatchingMediaQueries } from "Store/browser";
import { MediaQuery } from "Config/media_queries";
import * as WPAPI from "wpapi";
import { Page } from "Entities/page";
import { v4 as uuid } from "uuid";
import { isEqual } from "lodash";

type StorePage = Store["pages"]["pages"][0];

function updateStatus(): UpdateStatus {
  return {
    type: "APPLICATION__UPDATE_STATUS",
    status: "ready"
  };
}

/**
 * Checks the DOM for a hidden input with a name "preview_flag", indicating
 * that the user is logged in, has the ability to edit posts, and may want to
 * preview their work on the site.
 *
 * @return The resulting value of the flag.
 */
function getPreviewFlag(): boolean {
  return document.querySelector('input[name="preview_flag"]') !== null;
}

function setPreviewFlag(
  dispatch: ThunkDispatch<Store, undefined, SetPreviewFlag>,
  previewFlag: boolean
): void {
  if (previewFlag === true) {
    dispatch({
      type: "APPLICATION__SET_PREVIEW_FLAG",
      preview_flag: true
    });
  }
}

/**
 * Checks a provided array of MQTMediaQueries against the current matches in the
 * Store.  If the two sets of media queries do **not** match, dispatches an
 * action to update the Store with the new set.
 *
 * @param matches An array of MQTMediaQueries that match the current
 * environment.
 * @return A Thunk Action which dispatches a new array of matching media queries
 * to the store, or returns void if the matches are already equal.
 */
function updateMatchingMediaQueries(
  matches: UpdateMatchingMediaQueries["matches"]
): ThunkAction<
  UpdateMatchingMediaQueries | void,
  Store,
  undefined,
  UpdateMatchingMediaQueries
> {
  return (dispatch, getState) => {
    const currentMatches = getState().browser.matching_media_queries;
    if (!isEqual(currentMatches, matches)) {
      dispatch({ type: "BROWSER__UPDATE_MATCHING_MEDIA_QUERIES", matches });
    }
  };
}

/**
 * Initializes the Media Query Tracker with the standard media queries and
 * a callback to update the store so that the application will have up to
 * date query matches at sensible times, e.g. orientation state change.
 * @param dispatch Reference to the dispatch method for updating the Store.
 * @param queries The set of queries to use when configuring the Media Query
 * Tracker.
 */
function trackMediaQueries(
  dispatch: ThunkDispatch<Store, undefined, UpdateMatchingMediaQueries>,
  queries: MediaQuery[]
): void {
  const tracker = new MediaQueryTracker(
    queries,
    [
      { event: "load", throttle: 0 },
      { event: "resize", throttle: 500 },
      { event: "orientationchange", throttle: 500 }
    ],
    e => dispatch(updateMatchingMediaQueries(e.matches))
  );
  dispatch(updateMatchingMediaQueries(tracker.getMatches()));
}

/**
 * Hits the API endpoint for pages and parses the response with any applicable
 * filters.
 *
 * @return An array of page information with which to populate the Store.
 */
function getPages(dispatch: ThunkDispatch<Store, undefined, SetPages>) {
  const api = new WPAPI({
    endpoint:
      process.env.NODE_ENV === "production"
        ? `https://www.thesandpipernurseryschool.com/wp-json`
        : `http://www.thesandpipernurseryschool.local/wp-json`
  }).setHeaders({
    "Cache-Control": "no-cache"
  });
  api
    .pages()
    .then(pages => {
      const coerced = (pages as unknown) as Page[];
      const mapped = coerced.map(page => mapPage(page));
      const filtered: StorePage[] = mapped.filter(
        page => page !== null
      ) as StorePage[];

      dispatch(setPages(filtered));
      dispatch(setActiveRoute(window.location.pathname));
    })
    .catch(error => {
      console.error(error);
    });
}

function setPages(
  pages: StorePage[]
): ThunkAction<SetPages, Store, undefined, SetPages> {
  return (dispatch, getState) => {
    const existing = getState().pages.pages;
    const combined = [...existing, ...pages];
    dispatch(setRoutesFromPages(combined));
    return dispatch({ type: "PAGES__SET_PAGES", pages: combined });
  };
}

function setRoutesFromPages(
  pages: StorePage[]
): ThunkAction<SetRoutes | MapRoutes, Store, undefined, SetRoutes> {
  return (dispatch, getState) => {
    const staticRoutes = getState().application.routes;
    const dynamicRoutes: Store["application"]["routes"] = pages
      .filter(page => page.is_navigable === true)
      .map(page => ({ id: uuid(), path: `/${page.slug}` }));
    const routes = [...staticRoutes, ...dynamicRoutes];

    window.addEventListener("popstate", (e: PopStateEvent) => {
      dispatch(navigate(new URL(window.location.href), false));
    });

    dispatch(mapRoutesToPages(routes, pages));
    return dispatch(setRoutes(routes));
  };
}

function mapRoutesToPages(
  routes: Store["application"]["routes"],
  pages: Store["pages"]["pages"]
): ThunkAction<MapRoutes, Store, undefined, MapRoutes> {
  return dispatch => {
    const mappings: Store["application"]["route_page"] = [];
    const navigablePages = pages
      .filter(page => page.is_navigable === true)
      .sort((a, b) => a.menu_order - b.menu_order);

    routes.forEach(route => {
      navigablePages.forEach(page => {
        if (`/${page.slug}` === route.path) {
          mappings.push({
            route: route.id,
            page: page.id
          });
        }
      });

      if (route.path === "/") {
        mappings.push({ route: route.id, page: navigablePages[0].id });
      }
    });

    return dispatch({ type: "APLLICATION__MAP_ROUTES", mappings });
  };
}

/**
 * Maps a raw page node from an API response to a page as used by the store.
 * If an error occurs during the mapping process, this method returns null.
 *
 * @param page A raw page node from the API response.
 * @return A valid Page entity or a null value if an error occurred.
 */
function mapPage(page: Page): StorePage | null {
  let result: StorePage | null = null;
  try {
    result = {
      id: uuid(),
      slug: page.slug,
      content: page.content.rendered,
      is_navigable: page.is_navigable === "true" ? true : false,
      menu_order: page.menu_order,
      title: page.title.rendered,
      context: page.has_hero_image === "true" ? "HEROIC" : "GENERIC"
    };
  } catch (error) {
    console.error(error);
    result = null;
  } finally {
    return result;
  }
}

/** Coordinates bootstrapping the application. */
export function bootstrap(): ThunkAction<
  Promise<UpdateStatus | void> | void,
  Store,
  undefined,
  UpdateStatus | SetPages
> {
  return (dispatch, getState) => {
    trackMediaQueries(dispatch, getState().browser.possible_media_queries);

    const previewFlag = getPreviewFlag();
    setPreviewFlag(dispatch, previewFlag);

    getPages(dispatch);

    dispatch(updateStatus());
  };
}
